<script setup>
import { computed, ref } from "vue";
import { LogIn, UserPlus } from "lucide-vue-next";
import { request, storeUser } from "../api";

const logoUrl = "/toktok-logo.png";

const emit = defineEmits(["authenticated"]);

const mode = ref("login");
const username = ref("");
const displayName = ref("");
const password = ref("");
const error = ref("");
const busy = ref(false);

const isRegister = computed(() => mode.value === "register");
const passwordStrength = computed(() => {
  if (!password.value) return 0;
  let score = 0;
  if (password.value.length >= 8) score += 1;
  if (/[a-z]/.test(password.value) && /[A-Z]/.test(password.value)) score += 1;
  if (/\d/.test(password.value)) score += 1;
  if (/[^A-Za-z0-9]/.test(password.value)) score += 1;
  return score;
});
const passwordStrengthLabel = computed(() => ["", "Weak", "Fair", "Good", "Strong"][passwordStrength.value]);
const passwordStrengthColor = computed(() => ["#e6e9e5", "#c95b45", "#e4a72c", "#74a83b", "#176b2a"][passwordStrength.value]);

async function submit() {
  error.value = "";
  if (isRegister.value && passwordStrength.value < 3) {
    error.value = "Password is too weak. Use at least 8 characters with uppercase, lowercase, and numbers.";
    return;
  }
  busy.value = true;
  try {
    const result = await request(isRegister.value ? "/api/register" : "/api/login", {
      method: "POST",
      body: JSON.stringify({
        username: username.value,
        display_name: displayName.value || username.value,
        password: password.value,
      }),
    });
    const authenticatedUser = { ...result.user, access_token: result.access_token };
    storeUser(authenticatedUser);
    emit("authenticated", authenticatedUser);
  } catch (err) {
    error.value = err.message;
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <main class="auth-shell grid min-h-screen place-items-center px-5 py-10">
    <section class="grid w-full max-w-5xl overflow-hidden rounded-[24px] border border-white/70 bg-white/95 shadow-[0_24px_80px_rgba(0,58,15,0.18)] md:grid-cols-[1fr_420px]" style="border-radius: 24px">
      <div class="auth-brand-panel relative overflow-hidden p-8 text-white md:p-10">
        <div class="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/25"></div>
        <div class="relative">
          <img :src="logoUrl" alt="TokTok" class="h-24 w-24 object-contain shadow-panel" style="border-radius: 8px" />
          <p class="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">Premium home and office goods</p>
          <h1 class="mt-3 text-4xl font-semibold tracking-tight">TokTok</h1>
        </div>
        <div class="relative mt-8 space-y-4 text-sm leading-6 text-white/80">
          <p>Sign in to manage your cart and review recent orders.</p>
          <p>Create an account to start shopping and save your order history.</p>
          <p>Registered customers can review products after completing a purchase.</p>
        </div>
      </div>

      <form class="p-6 md:p-8" @submit.prevent="submit">
        <div class="mb-6 flex border border-line bg-field p-1" style="border-radius: 8px">
          <button
            type="button"
            class="flex flex-1 items-center justify-center gap-2 px-3 py-2 text-sm font-semibold"
            :class="mode === 'login' ? 'bg-white shadow-sm' : 'text-[#68635d]'"
            @click="mode = 'login'"
          >
            <LogIn class="h-4 w-4" />
            Login
          </button>
          <button
            type="button"
            class="flex flex-1 items-center justify-center gap-2 px-3 py-2 text-sm font-semibold"
            :class="mode === 'register' ? 'bg-white shadow-sm' : 'text-[#68635d]'"
            @click="mode = 'register'"
          >
            <UserPlus class="h-4 w-4" />
            Register
          </button>
        </div>

        <h2 class="text-2xl font-semibold tracking-tight">{{ isRegister ? "Create User" : "Welcome Back" }}</h2>
        <p class="mt-2 text-sm text-[#68635d]">Access your cart, orders, and product recommendations.</p>

        <label class="mt-6 block text-sm font-medium text-[#4d4944]" for="username">Username</label>
        <input
          id="username"
          v-model="username"
          class="mt-2 w-full border border-line bg-field px-3 py-2 text-sm outline-none focus:border-moss"
          style="border-radius: 8px"
          autocomplete="username"
        />

        <label v-if="isRegister" class="mt-4 block text-sm font-medium text-[#4d4944]" for="displayName">Display name</label>
        <input
          v-if="isRegister"
          id="displayName"
          v-model="displayName"
          class="mt-2 w-full border border-line bg-field px-3 py-2 text-sm outline-none focus:border-moss"
          style="border-radius: 8px"
          autocomplete="name"
        />

        <label class="mt-4 block text-sm font-medium text-[#4d4944]" for="password">Password</label>
        <input
          id="password"
          v-model="password"
          type="password"
          class="mt-2 w-full border border-line bg-field px-3 py-2 text-sm outline-none focus:border-moss"
          style="border-radius: 8px"
          :autocomplete="isRegister ? 'new-password' : 'current-password'"
        />

        <div v-if="isRegister" class="mt-3">
          <div class="flex gap-1.5" aria-label="Password strength">
            <span v-for="bar in 4" :key="bar" class="h-1.5 flex-1 rounded-full" :style="{ backgroundColor: bar <= passwordStrength ? passwordStrengthColor : '#e6e9e5' }"></span>
          </div>
          <div class="mt-2 text-xs">
            <span :class="passwordStrength ? 'font-semibold text-ink' : 'text-[#8a918b]'">{{ passwordStrengthLabel || "Check password strength" }}</span>
          </div>
        </div>

        <p v-if="error" class="mt-4 border border-ember bg-[#fff1ec] px-3 py-2 text-sm text-ember" style="border-radius: 8px">
          {{ error }}
        </p>

        <button class="primary-button mt-6 w-full" :disabled="busy">
          <component :is="isRegister ? UserPlus : LogIn" class="h-4 w-4" />
          {{ isRegister ? "Register" : "Login" }}
        </button>
      </form>
    </section>
  </main>
</template>

